import React from 'react';
import {
    Typography,
    Box,
    List,
    ListItem,
    ListItemButton,
    Chip,
    Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getPriorityColor, getPriorityLabel } from '../../models/TaskDTO';
import WidgetCard from './WidgetCard';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { useTasksServiceGetApiTasksPaginated } from '../../api/generated/queries/queries';

const OverdueTasksWidget: React.FC = () => {

    // Fetch overdue tasks
    const { data: taskPagedResponse, isLoading } = useTasksServiceGetApiTasksPaginated({
        page: 0,
        size: 5, // Get more tasks to show all overdue tasks
        view: 'OVERDUE',
        sortBy: 'CREATED_AT',
        sortDirection: 'DESC'
    });

    // Extract data from response
    const tasks = taskPagedResponse?.content || [];

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Brak terminu';
        return format(new Date(dateString), 'dd.MM.yyyy');
    };

    return (
        <WidgetCard>
            <Box
                mb={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pb={1}
                borderBottom="1px solid"
                borderColor="divider"
            >
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            backgroundColor: '#fdecea',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                        }}
                    >
                        <AssignmentLateIcon sx={{ color: 'error.main', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
                        Zaległe zadania
                    </Typography>
                </Box>

                <Button
                    component={Link}
                    to="/tasks"
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.25,
                        height: '24px',
                        minWidth: 'auto',
                        lineHeight: 1,
                    }}
                >
                    Zobacz wszystkie
                </Button>
            </Box>


            <List disablePadding>
                {isLoading ? (
                    <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                        <Typography fontSize="0.875rem">Ładowanie...</Typography>
                    </ListItem>
                ) : tasks.length > 0 ? tasks.map((task, index) => (
                    <ListItem
                        key={task.id}
                        disablePadding
                        sx={{
                            borderBottom: index !== tasks.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                        }}
                    >
                        <ListItemButton
                            component={Link}
                            to={`/tasks?taskId=${task.id}`}
                            sx={{
                                px: 0,
                                py: 0.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                    borderRadius: 1,
                                },
                                transition: 'background-color 0.2s ease',
                                textDecoration: 'none',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mr: 1 }}>
                                <Typography
                                    fontSize="0.85rem"
                                    fontWeight={500}
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {task.title}
                                </Typography>
                                <Typography fontSize="0.7rem" color="text.secondary">
                                    Termin: {formatDate(task.endDate)}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={getPriorityLabel(task.priority)}
                                sx={{
                                    bgcolor: getPriorityColor(task.priority),
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: 22,
                                    pointerEvents: 'none', // Zapobiega kliknięciom na chip
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                )) : (
                    <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                        <Typography fontSize="0.875rem">
                            Brak zaległych zadań
                        </Typography>
                    </ListItem>
                )}
            </List>

        </WidgetCard>
    );
};

export default OverdueTasksWidget;